import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import {
  MdArrowBack,
  MdCalendarToday,
  MdPerson,
  MdComment,
  MdSend,
  MdAdminPanelSettings,
  MdReply,
  MdClose,
  MdFlag,
  MdSwapHoriz,
  MdAttachFile,
  MdPictureAsPdf,
  MdDescription,
  MdTableChart,
  MdInsertDriveFile,
  MdImage,
  MdFileDownload,
  MdDelete,
  MdExpandMore,
} from "react-icons/md";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import {
  renderTextWithMentions,
  insertMentionAtCursor,
  toBackendMentionFormat,
} from "../utils/mentionUtils";
import { canMarkTaskComplete } from "../utils/taskPermissions";
import { handlePendingDoneRequestResponse } from "../utils/taskUpdateHelpers";
import { getUserId } from "../utils/userIds";
import { format, isBefore, addDays } from "date-fns";
import "./TaskDetail.css";

const API_URL =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_URL || window.location.origin
    : "http://localhost:5000";

const COMMENT_REACTION_EMOJIS = [
  "👍",
  "❤️",
  "😂",
  "😮",
  "🙏",
  "👏",
  "✅",
  "👀",
];

/** Aggregate reactions by emoji for display (count, whether current user reacted, tooltip names). */
const groupCommentReactions = (reactions, currentUserId) => {
  if (!Array.isArray(reactions) || reactions.length === 0) return [];
  const map = new Map();
  for (const r of reactions) {
    const em = r.emoji;
    if (!em) continue;
    const rid = r.user?._id ?? r.user;
    const name = r.user?.name || "Someone";
    if (!map.has(em))
      map.set(em, { emoji: em, count: 0, mine: false, names: [] });
    const row = map.get(em);
    row.count += 1;
    row.names.push(name);
    if (currentUserId != null && String(rid) === String(currentUserId))
      row.mine = true;
  }
  return Array.from(map.values())
    .map((row) => ({
      ...row,
      title: row.names.filter(Boolean).join(", "),
    }))
    .sort((a, b) => b.count - a.count);
};

const getInitials = (name) =>
  name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
const avatarColors = ["#6366f1", "#f59e0b", "#10b981", "#ef4444", "#3b82f6"];
const avatarColor = (name) =>
  avatarColors[name?.charCodeAt(0) % avatarColors.length] || "#6366f1";

/** React Router state.returnTo — only same-app list pages (no open redirects). */
const safeTaskListReturnTo = (raw) => {
  if (typeof raw !== "string" || !raw.startsWith("/") || raw.startsWith("//"))
    return null;
  const noHash = raw.split("#")[0];
  const q = noHash.indexOf("?");
  const path = q >= 0 ? noHash.slice(0, q) : noHash;
  const search = q >= 0 ? noHash.slice(q) : "";
  const allowed =
    path === "/admin/tasks" || path === "/tasks" || path === "/completed-tasks";
  if (!allowed) return null;
  return `${path}${search}`;
};

const getCommentFileIcon = (mimeType, originalName) => {
  const m = (mimeType || "").toLowerCase();
  const name = (originalName || "").toLowerCase();
  if (m.includes("pdf") || name.endsWith(".pdf")) return MdPictureAsPdf;
  if (
    m.includes("word") ||
    m.includes("document") ||
    name.endsWith(".doc") ||
    name.endsWith(".docx")
  )
    return MdDescription;
  if (
    m.includes("sheet") ||
    m.includes("excel") ||
    name.endsWith(".xls") ||
    name.endsWith(".xlsx") ||
    name.endsWith(".csv")
  )
    return MdTableChart;
  if (m.startsWith("image/")) return MdImage;
  return MdInsertDriveFile;
};

const getAttachmentFileIcon = (mimeType, originalName) =>
  getCommentFileIcon(mimeType, originalName);

const UserAvatar = ({ user, size = 32 }) => {
  if (!user) return null;
  const imgSrc = user.avatar
    ? user.avatar.startsWith("http")
      ? user.avatar
      : `${API_URL}${user.avatar}`
    : null;
  return (
    <div
      className="task-detail-avatar"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.35,
        background: imgSrc ? "transparent" : avatarColor(user.name),
      }}
      title={user.name}
    >
      {imgSrc ? (
        <img
          src={imgSrc}
          alt={user.name}
          onError={(e) => {
            e.target.style.display = "none";
            e.target.parentNode.textContent = getInitials(user.name);
          }}
        />
      ) : (
        getInitials(user.name)
      )}
    </div>
  );
};

const TaskDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [commentFile, setCommentFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reactionBusyCommentId, setReactionBusyCommentId] = useState(null);
  const [users, setUsers] = useState([]);
  const [assignSearch, setAssignSearch] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionStart, setMentionStart] = useState(-1);
  const [mentionSearch, setMentionSearch] = useState("");
  const bottomRef = useRef();
  const inputRef = useRef();
  const attachInputRef = useRef(null);
  const assignPickerRef = useRef(null);
  const commentFileInputRef = useRef(null);
  const commentMentionListRef = useRef([]);

  const backHref = safeTaskListReturnTo(location.state?.returnTo) || "/tasks";
  const backLabel = backHref.startsWith("/admin/tasks")
    ? "Back to Task Management"
    : backHref.startsWith("/completed-tasks")
      ? "Back to Completed Tasks"
      : "Back to My Tasks";

  const fetchTask = async () => {
    try {
      const { data } = await api.get(`/tasks/${id}`);
      setTask(data.data);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error("Task not found");
        navigate(safeTaskListReturnTo(location.state?.returnTo) || "/tasks");
      } else {
        toast.error("Failed to load task");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/list");
        setUsers(data.data || []);
      } catch {
        // ignore user list errors here
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (task?.comments?.length != null)
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [task?.comments?.length]);

  useEffect(() => {
    if (!assignOpen) return;
    const onDown = (e) => {
      if (
        assignPickerRef.current &&
        !assignPickerRef.current.contains(e.target)
      ) {
        setAssignOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setAssignOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [assignOpen]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() && !commentFile) return;
    setSubmitting(true);
    try {
      const textForApi = toBackendMentionFormat(
        commentText.trim(),
        commentMentionListRef.current,
      );
      if (commentFile) {
        const fd = new FormData();
        fd.append("file", commentFile);
        if (textForApi) fd.append("text", textForApi);
        if (replyTo) fd.append("replyTo", replyTo._id);
        await api.post(`/tasks/${id}/comments/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        const payload = { text: textForApi };
        if (replyTo) payload.replyTo = replyTo._id;
        await api.post(`/tasks/${id}/comments`, payload);
      }
      setCommentText("");
      setReplyTo(null);
      setCommentFile(null);
      commentMentionListRef.current = [];
      if (commentFileInputRef.current) commentFileInputRef.current.value = "";
      await fetchTask();
      toast.success("Comment added");
    } catch {
      toast.error("Failed to post comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCommentFileChange = (e) => {
    const file = e.target.files?.[0];
    setCommentFile(file || null);
  };

  const clearCommentFile = () => {
    setCommentFile(null);
    if (commentFileInputRef.current) commentFileInputRef.current.value = "";
  };

  const handleReply = (comment) => {
    setReplyTo({ _id: comment.user?._id, name: comment.user?.name });
    setCommentText("");
    setCommentFile(null);
    commentMentionListRef.current = [];
    setMentionOpen(false);
    inputRef.current?.focus();
  };

  const handleToggleCommentReaction = async (commentId, emoji) => {
    const cid = commentId != null ? String(commentId) : "";
    if (!cid || reactionBusyCommentId) return;
    setReactionBusyCommentId(cid);
    try {
      await api.post(`/tasks/${id}/comments/${cid}/reactions`, { emoji });
      await fetchTask();
    } catch {
      toast.error("Could not update reaction");
    } finally {
      setReactionBusyCommentId(null);
    }
  };

  const handleCommentChange = (e) => {
    const val = e.target.value;
    const pos = e.target.selectionStart ?? val.length;
    setCommentText(val);
    const beforeCursor = val.slice(0, pos);
    const atIdx = beforeCursor.lastIndexOf("@");
    if (atIdx !== -1 && (atIdx === 0 || /\s/.test(val[atIdx - 1]))) {
      setMentionStart(atIdx);
      setMentionSearch(beforeCursor.slice(atIdx + 1).toLowerCase());
      setMentionOpen(true);
    } else {
      setMentionOpen(false);
    }
  };

  const handleSelectMention = (u) => {
    const start = mentionStart;
    const cursorPos = inputRef.current?.selectionStart ?? commentText.length;
    const countBefore = (commentText.slice(0, start).match(/@/g) || []).length;
    const list = commentMentionListRef.current;
    list.splice(countBefore, 0, { userId: u._id, displayName: u.name });
    const { newValue, newPos } = insertMentionAtCursor(
      commentText,
      start,
      cursorPos,
      u._id,
      u.name,
    );
    setCommentText(newValue);
    setMentionOpen(false);
    setMentionSearch("");
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const mentionFiltered = mentionSearch
    ? users.filter(
        (u) =>
          u._id !== user?._id &&
          (u.name || "").toLowerCase().includes(mentionSearch),
      )
    : users.filter((u) => u._id !== user?._id);

  const handleDownloadCommentAttachment = (filename, originalName) => {
    api
      .get(
        `/tasks/files/${encodeURIComponent(filename)}?name=${encodeURIComponent(originalName || filename)}`,
        {
          responseType: "blob",
        },
      )
      .then((res) => {
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = originalName || filename;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Download failed"));
  };

  const handleDownloadTaskAttachment = (filename, originalName) => {
    api
      .get(
        `/tasks/files/${encodeURIComponent(filename)}?name=${encodeURIComponent(originalName || filename)}`,
        {
          responseType: "blob",
        },
      )
      .then((res) => {
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = originalName || filename;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Download failed"));
  };

  const handleDeleteTaskAttachment = async (filename) => {
    if (!window.confirm("Delete this attachment from the task?")) return;
    try {
      const { data } = await api.delete(
        `/tasks/${task._id}/attachments/${encodeURIComponent(filename)}`,
      );
      setTask(data.data);
      toast.success("Attachment deleted");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to delete attachment",
      );
    }
  };

  const handleQuickUpdate = async (patch) => {
    if (!task) return;
    const me = user?._id ?? user?.id;
    if (
      patch.status === "done" &&
      task.status !== "done" &&
      !canMarkTaskComplete(me, task)
    ) {
      toast.error(
        "Only the task creator or assigned users can mark this task as complete",
      );
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: task.title,
        description: task.description,
        assignedTo: task.assignedTo?._id || undefined,
        assignedUsers: Array.isArray(task.assignedUsers)
          ? task.assignedUsers.map((u) => (u?._id ? u._id : u)).filter(Boolean)
          : task.assignedTo?._id
            ? [task.assignedTo._id]
            : [],
        status: task.status,
        priority: task.priority,
        deadline: task.deadline,
        tags: task.tags || [],
        ...patch,
      };
      const { data } = await api.put(`/tasks/${id}`, payload);
      setTask(data.data);
      if (!handlePendingDoneRequestResponse(data, toast)) {
        toast.success("Task updated");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update task");
    } finally {
      setSaving(false);
    }
  };

  const handleAttachChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !task) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/tasks/${task._id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTask(data.data);
      toast.success("Attachment uploaded");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to upload attachment");
    } finally {
      if (attachInputRef.current) attachInputRef.current.value = "";
    }
  };

  if (loading)
    return (
      <div className="page">
        <div className="loading" style={{ minHeight: 300 }}>
          <div className="spinner" />
          <span className="loading-text">Loading...</span>
        </div>
      </div>
    );

  if (!task)
    return (
      <div className="page">
        <p>Task not found.</p>
        <Link to={backHref}>{backLabel}</Link>
      </div>
    );

  const hasDeadline = !!task.deadline;
  const deadlineDate = hasDeadline ? new Date(task.deadline) : null;
  const now = new Date();
  const isDone =
    (task.status || "").toLowerCase() === "done" ||
    (task.status || "").toLowerCase() === "completed" ||
    (task.status || "").toLowerCase() === "closed";
  const isOverdue = hasDeadline && !isDone && isBefore(deadlineDate, now);
  const isDueSoon =
    hasDeadline &&
    !isDone &&
    !isOverdue &&
    isBefore(deadlineDate, addDays(now, 3));
  const idOf = (value) =>
    value && typeof value === "object" ? value._id : value;
  const me = user?._id ?? user?.id;
  const maySetDone =
    canMarkTaskComplete(me, task) || (task.status || "") === "done";
  const assignedUsers =
    Array.isArray(task.assignedUsers) && task.assignedUsers.length
      ? task.assignedUsers
      : task.assignedTo
        ? [task.assignedTo]
        : [];
  const assignFiltered = assignSearch
    ? users.filter((u) =>
        `${u.name || ""} ${u.email || ""}`
          .toLowerCase()
          .includes(assignSearch.toLowerCase()),
      )
    : users;
  const displayAssignedUsers = assignedUsers.filter(
    (assignedUser) => idOf(assignedUser) !== idOf(task.createdBy),
  );
  const isSelfAssigned =
    assignedUsers.length > 0 && displayAssignedUsers.length === 0;
  const assignSummary =
    assignedUsers.length === 0
      ? "Choose assignees"
      : assignedUsers.length === 1
        ? (() => {
            const n = assignedUsers[0]?.name || "1 person";
            return n.length > 28 ? `${n.slice(0, 26)}…` : n;
          })()
        : `${assignedUsers.length} people assigned`;

  return (
    <main className="page task-detail-page">
      <input
        ref={attachInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={handleAttachChange}
      />
      <div className="task-detail-header">
        <Link to={backHref} className="task-detail-back">
          <MdArrowBack size={20} /> {backLabel}
        </Link>
        <div className="task-detail-header-body">
          <div className="task-detail-header-title-row">
            <div className="task-detail-title-wrap">
              <h1 className="task-detail-title">{task.title}</h1>
            </div>
            <div className="task-detail-header-toolbar">
              <div className="task-detail-toolbar-well">
                <div
                  ref={assignPickerRef}
                  className="task-detail-toolbar-field task-detail-toolbar-field--assign"
                >
                  <span className="task-detail-toolbar-label">
                    <MdPerson size={15} aria-hidden /> Assign
                  </span>
                  <button
                    type="button"
                    className={`task-detail-assign-trigger${assignOpen ? " is-open" : ""}`}
                    onClick={() => setAssignOpen((o) => !o)}
                    disabled={saving}
                    aria-expanded={assignOpen}
                    aria-haspopup="listbox"
                  >
                    <span className="task-detail-assign-trigger-text">
                      {assignSummary}
                    </span>
                    <MdExpandMore
                      size={20}
                      aria-hidden
                      className="task-detail-assign-chevron"
                    />
                  </button>
                  {assignOpen && (
                    <div
                      className="task-detail-assign-panel"
                      role="listbox"
                      aria-label="Assign users"
                    >
                      <input
                        className="form-control task-detail-assign-search"
                        placeholder="Search users…"
                        value={assignSearch}
                        onChange={(e) => setAssignSearch(e.target.value)}
                        autoFocus
                      />
                      <div className="task-detail-assign-list">
                        {assignFiltered.map((u) => {
                          const checked = assignedUsers.some(
                            (x) => idOf(x) === u._id,
                          );
                          return (
                            <label
                              key={u._id}
                              className="task-detail-assign-option"
                              style={{
                                cursor: saving ? "not-allowed" : "pointer",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={saving}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  const currentIds = assignedUsers
                                    .map((x) => idOf(x))
                                    .filter(Boolean);
                                  const nextIds = isChecked
                                    ? [...new Set([...currentIds, u._id])]
                                    : currentIds.filter((id) => id !== u._id);
                                  handleQuickUpdate({
                                    assignedUsers: nextIds,
                                    assignedTo: nextIds[0] || undefined,
                                  });
                                }}
                              />
                              <span>{u.name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <div className="task-detail-toolbar-field">
                  <span className="task-detail-toolbar-label">
                    <MdSwapHoriz size={15} aria-hidden /> Status
                  </span>
                  <select
                    className="task-detail-select task-detail-select--toolbar"
                    value={task.status}
                    onChange={(e) =>
                      handleQuickUpdate({ status: e.target.value })
                    }
                    disabled={saving}
                    title={
                      !maySetDone && (task.status || "") !== "done"
                        ? "Only the creator or assigned users can mark this task complete"
                        : undefined
                    }
                  >
                    <option value="todo">To Do</option>
                    <option value="in-progress">In Progress</option>
                    <option value="review">Review</option>
                    {maySetDone && <option value="done">Done</option>}
                  </select>
                </div>
                <div className="task-detail-toolbar-field">
                  <span className="task-detail-toolbar-label">
                    <MdFlag size={15} aria-hidden /> Priority
                  </span>
                  <select
                    className="task-detail-select task-detail-select--toolbar"
                    value={task.priority}
                    onChange={(e) =>
                      handleQuickUpdate({ priority: e.target.value })
                    }
                    disabled={saving}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className="task-detail-meta-row">
            <span className={`badge badge-${task.status}`}>{task.status}</span>
            <span className={`badge badge-${task.priority}`}>
              {task.priority}
            </span>
            {hasDeadline && (
              <span
                className={`task-detail-deadline ${
                  isOverdue
                    ? "task-detail-deadline-overdue"
                    : isDueSoon
                      ? "task-detail-deadline-soon"
                      : ""
                }`}
              >
                <MdCalendarToday size={18} />
                <span className="task-detail-deadline-text">
                  {format(deadlineDate, "MMM d, yyyy")}
                  {isOverdue && " · Overdue"}
                  {isDueSoon && !isOverdue && " · Due soon"}
                </span>
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="task-detail-grid">
        <div className="task-detail-main">
          {task.description && (
            <section className="task-detail-section">
              <h3>Description</h3>
              <p className="task-detail-description">
                {renderTextWithMentions(task.description, task._id)}
              </p>
            </section>
          )}

          {task.tags?.length > 0 && (
            <section className="task-detail-section">
              <h3>Tags</h3>
              <div className="task-detail-tags">
                {task.tags.map((tag) => (
                  <span key={tag} className="task-detail-tag">
                    {tag}
                  </span>
                ))}
              </div>
            </section>
          )}

          <section className="task-detail-section">
            <h3>
              <MdAttachFile size={18} /> Attachments
            </h3>
            <div className="task-detail-attachments">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => attachInputRef.current?.click()}
              >
                <MdAttachFile size={14} /> Add attachment
              </button>
              {!task.attachments?.length ? (
                <p className="task-detail-no-attachments">
                  No attachments yet for this task.
                </p>
              ) : (
                <div className="task-detail-attachments-grid">
                  {task.attachments.map((a, i) => {
                    const isImage = (a.mimeType || "").startsWith("image/");
                    const fileUrl = `${API_URL}${a.url}`;
                    const FileIcon = getAttachmentFileIcon(
                      a.mimeType,
                      a.originalName,
                    );
                    return (
                      <div key={i} className="task-detail-attachment-card">
                        {isImage ? (
                          <>
                            <div className="task-detail-attachment-preview">
                              <img src={fileUrl} alt={a.originalName} />
                            </div>
                            <div className="task-detail-attachment-info">
                              <span
                                className="task-detail-attachment-name"
                                title={a.originalName}
                              >
                                {a.originalName}
                              </span>
                              <div className="task-detail-attachment-actions">
                                <button
                                  type="button"
                                  className="task-detail-attachment-download-btn"
                                  onClick={() =>
                                    handleDownloadTaskAttachment(
                                      a.filename,
                                      a.originalName,
                                    )
                                  }
                                  title="Download attachment"
                                >
                                  <MdFileDownload size={17} />
                                </button>
                                <button
                                  type="button"
                                  className="task-detail-attachment-delete-btn"
                                  onClick={() =>
                                    handleDeleteTaskAttachment(a.filename)
                                  }
                                  title="Delete attachment"
                                >
                                  <MdDelete size={17} />
                                </button>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="task-detail-attachment-doc-icon">
                              <FileIcon size={40} />
                            </div>
                            <div className="task-detail-attachment-info">
                              <span
                                className="task-detail-attachment-name"
                                title={a.originalName}
                              >
                                {a.originalName}
                              </span>
                              <div className="task-detail-attachment-actions">
                                <button
                                  type="button"
                                  className="task-detail-attachment-download-btn"
                                  onClick={() =>
                                    handleDownloadTaskAttachment(
                                      a.filename,
                                      a.originalName,
                                    )
                                  }
                                  title="Download attachment"
                                >
                                  <MdFileDownload size={17} />
                                </button>
                                <button
                                  type="button"
                                  className="task-detail-attachment-delete-btn"
                                  onClick={() =>
                                    handleDeleteTaskAttachment(a.filename)
                                  }
                                  title="Delete attachment"
                                >
                                  <MdDelete size={17} />
                                </button>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          <section className="task-detail-section task-detail-comments">
            <h3>
              <MdComment size={20} /> Comments ({task.comments?.length || 0})
            </h3>

            <div className="task-detail-comment-list">
              {!task.comments?.length ? (
                <p className="task-detail-no-comments">
                  No comments yet. Start the conversation below.
                </p>
              ) : (
                task.comments.map((c, i) => {
                  const authorId = getUserId(c.user);
                  const currentUserId = getUserId(user);
                  const isMine =
                    currentUserId != null &&
                    authorId != null &&
                    String(authorId) === String(currentUserId);
                  const prev = i > 0 ? task.comments[i - 1] : null;
                  const prevAuthorId = prev ? getUserId(prev.user) : null;
                  const isGrouped =
                    prev &&
                    authorId != null &&
                    prevAuthorId != null &&
                    String(authorId) === String(prevAuthorId);
                  const reactionGroups = groupCommentReactions(
                    c.reactions,
                    currentUserId,
                  );
                  const createdAt = new Date(c.createdAt);
                  const commentDateTime = format(
                    createdAt,
                    "MMM d, yyyy 'at' HH:mm",
                  );
                  return (
                  <div
                    key={c._id ?? i}
                    className={`task-detail-comment-row ${
                      c.isAdminComment ? "task-detail-comment-row--admin" : ""
                    } ${isMine ? "task-detail-comment-row--mine" : ""} ${
                      isGrouped ? "task-detail-comment-row--grouped" : ""
                    }`}
                  >
                    {!isMine &&
                      (isGrouped ? (
                        <div
                          className="task-detail-comment-avatar-spacer"
                          aria-hidden
                        />
                      ) : (
                        <div className="task-detail-comment-avatar-wrap">
                          <UserAvatar user={c.user} size={36} />
                        </div>
                      ))}
                    <div className="task-detail-comment-stack">
                      {!isMine && !isGrouped && (
                        <div className="task-detail-comment-sender">
                          <span className="task-detail-comment-sender-name">
                            {c.user?.name}
                          </span>
                          {c.user?.role === "admin" && (
                            <span className="task-detail-comment-admin-badge">
                              <MdAdminPanelSettings size={12} /> Admin
                            </span>
                          )}
                        </div>
                      )}
                      <div
                        className={`task-detail-comment-bubble ${
                          isMine
                            ? "task-detail-comment-bubble--sent"
                            : "task-detail-comment-bubble--received"
                        } ${c.isAdminComment ? "task-detail-comment-bubble--admin" : ""}`}
                      >
                        {c.replyTo && (
                          <div className="task-detail-comment-reply-quote">
                            <MdReply size={14} aria-hidden />
                            <span className="task-detail-comment-reply-quote-label">
                              {c.replyTo?.name}
                            </span>
                          </div>
                        )}
                        {!!c.text && (
                          <div className="task-detail-comment-text">
                            {renderTextWithMentions(c.text, task._id)}
                          </div>
                        )}
                        {Array.isArray(c.attachments) &&
                          c.attachments.length > 0 && (
                            <div className="task-detail-comment-attachments">
                              {c.attachments.map((att, idx) => {
                                const isImage = (att.mimeType || "").startsWith(
                                  "image/",
                                );
                                const fileUrl = `${API_URL}${att.url}`;
                                const FileIcon = getCommentFileIcon(
                                  att.mimeType,
                                  att.originalName,
                                );
                                return (
                                  <div
                                    key={idx}
                                    className="task-detail-comment-attachment-item"
                                  >
                                    {isImage ? (
                                      <>
                                        <div className="task-detail-comment-attachment-preview">
                                          <img
                                            src={fileUrl}
                                            alt={att.originalName}
                                          />
                                        </div>
                                        <button
                                          type="button"
                                          className="task-detail-comment-attachment-download"
                                          onClick={() =>
                                            handleDownloadCommentAttachment(
                                              att.filename,
                                              att.originalName,
                                            )
                                          }
                                        >
                                          <MdAttachFile size={14} /> Download
                                        </button>
                                      </>
                                    ) : (
                                      <button
                                        type="button"
                                        className="task-detail-comment-attachment-link"
                                        onClick={() =>
                                          handleDownloadCommentAttachment(
                                            att.filename,
                                            att.originalName,
                                          )
                                        }
                                      >
                                        <FileIcon
                                          size={20}
                                          className="task-detail-comment-attachment-icon"
                                        />
                                        <span className="task-detail-comment-attachment-name">
                                          {att.originalName}
                                        </span>
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        <div className="task-detail-comment-bubble-footer">
                          <time
                            className="task-detail-comment-time-in-bubble"
                            dateTime={createdAt.toISOString()}
                          >
                            {commentDateTime}
                          </time>
                        </div>
                      </div>
                      <div
                        className="task-detail-comment-toolbar"
                        dir="ltr"
                      >
                        <div className="task-detail-comment-reactions">
                          {reactionGroups.length > 0 && (
                            <div
                              className="task-detail-comment-reaction-chips"
                              role="group"
                              aria-label="Reactions on this comment"
                            >
                              {reactionGroups.map((rg) => (
                                <button
                                  key={rg.emoji}
                                  type="button"
                                  className={`task-detail-comment-reaction-chip${
                                    rg.mine
                                      ? " task-detail-comment-reaction-chip--mine"
                                      : ""
                                  }`}
                                  title={rg.title}
                                  disabled={!!reactionBusyCommentId}
                                  onClick={() =>
                                    handleToggleCommentReaction(
                                      c._id,
                                      rg.emoji,
                                    )
                                  }
                                >
                                  <span
                                    className="task-detail-comment-reaction-chip-emoji"
                                    aria-hidden
                                  >
                                    {rg.emoji}
                                  </span>
                                  <span className="task-detail-comment-reaction-chip-count">
                                    {rg.count}
                                  </span>
                                </button>
                              ))}
                            </div>
                          )}
                          <div
                            className="task-detail-comment-reaction-picker"
                            role="toolbar"
                            aria-label="Add a reaction"
                          >
                            {COMMENT_REACTION_EMOJIS.map((emoji) => (
                              <button
                                key={emoji}
                                type="button"
                                className="task-detail-comment-reaction-add"
                                title={`React with ${emoji}`}
                                disabled={!!reactionBusyCommentId}
                                onClick={() =>
                                  handleToggleCommentReaction(c._id, emoji)
                                }
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="task-detail-comment-reply-btn"
                          onClick={() => handleReply(c)}
                        >
                          <MdReply size={14} /> Reply
                        </button>
                      </div>
                    </div>
                    {isMine && (
                      <div className="task-detail-comment-avatar-wrap">
                        <UserAvatar user={user} size={36} />
                      </div>
                    )}
                  </div>
                  );
                })
              )}
              <div ref={bottomRef} />
            </div>

            {replyTo && (
              <div className="task-detail-reply-indicator">
                <MdReply size={14} />
                <span>
                  Replying to <strong>{replyTo.name}</strong>
                </span>
                <button type="button" onClick={() => setReplyTo(null)}>
                  <MdClose size={14} />
                </button>
              </div>
            )}
            {commentFile && (
              <div className="task-detail-reply-indicator">
                <MdAttachFile size={14} />
                <span>
                  Attaching <strong>{commentFile.name}</strong>
                </span>
                <button type="button" onClick={clearCommentFile}>
                  <MdClose size={14} />
                </button>
              </div>
            )}

            <input
              ref={commentFileInputRef}
              type="file"
              className="sr-only"
              aria-label="Attach file to comment"
              onChange={handleCommentFileChange}
            />
            <form
              className="task-detail-comment-form"
              onSubmit={handleSubmitComment}
            >
              <UserAvatar user={user} size={40} />
              <button
                type="button"
                className="task-detail-comment-attach-btn"
                onClick={() => commentFileInputRef.current?.click()}
                title="Attach file"
              >
                <MdAttachFile size={20} />
              </button>
              <div className="task-detail-comment-input-wrap">
                <input
                  ref={inputRef}
                  className="task-detail-comment-input"
                  value={commentText}
                  onChange={handleCommentChange}
                  onBlur={() => setTimeout(() => setMentionOpen(false), 200)}
                  placeholder={
                    replyTo
                      ? `Reply to ${replyTo.name}... (type @ to mention)`
                      : "Write a comment... (type @ to mention)"
                  }
                />
                {mentionOpen && mentionFiltered.length > 0 && (
                  <ul className="mention-dropdown" role="listbox">
                    {mentionFiltered.map((u) => (
                      <li
                        key={u._id}
                        role="option"
                        className="mention-dropdown-item"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSelectMention(u);
                        }}
                      >
                        <UserAvatar user={u} size={24} />
                        <span>{u.name}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                type="submit"
                className="btn btn-primary task-detail-comment-submit"
                disabled={submitting || (!commentText.trim() && !commentFile)}
              >
                <MdSend size={18} /> {submitting ? "Sending..." : "Send"}
              </button>
            </form>
          </section>
        </div>

        <aside className="task-detail-sidebar">
          <section className="task-detail-side-card">
            <h3>Assigned to</h3>
            {isSelfAssigned ? (
              <div className="task-detail-self-assigned">Self Assigned</div>
            ) : displayAssignedUsers.length > 0 ? (
              <div style={{ display: "grid", gap: 10 }}>
                {displayAssignedUsers.map((assignedUser) => (
                  <div
                    key={idOf(assignedUser)}
                    className="task-detail-user-row"
                  >
                    <UserAvatar user={assignedUser} size={40} />
                    <div className="task-detail-user-meta">
                      <div className="task-detail-user-name">
                        {assignedUser?.name || "User"}
                      </div>
                      {(assignedUser?.designation || assignedUser?.email) && (
                        <div className="task-detail-user-subtext">
                          {assignedUser.designation || assignedUser.email}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div className="task-detail-user-subtext">
                  Total assigned: {displayAssignedUsers.length}
                </div>
              </div>
            ) : (
              <p className="text-muted">Not assigned</p>
            )}
          </section>

          {task.coAssignedTo && (
            <section className="task-detail-side-card">
              <h3>
                Co-Assignee
                <span className="task-detail-role-chip">assistant</span>
              </h3>
              <div className="task-detail-user-row">
                <UserAvatar user={task.coAssignedTo} size={40} />
                <div className="task-detail-user-meta">
                  <div className="task-detail-user-name">
                    {task.coAssignedTo.name}
                  </div>
                  {(task.coAssignedTo.designation ||
                    task.coAssignedTo.email) && (
                    <div className="task-detail-user-subtext">
                      {task.coAssignedTo.designation || task.coAssignedTo.email}
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          <section className="task-detail-side-card">
            <h3>Created by</h3>
            {task.createdBy ? (
              <div className="task-detail-user-row">
                <UserAvatar user={task.createdBy} size={36} />
                <div className="task-detail-user-meta">
                  <div className="task-detail-user-name">
                    {task.createdBy.name}
                  </div>
                  {task.createdBy.email && (
                    <div className="task-detail-user-subtext">
                      {task.createdBy.email}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-muted">—</p>
            )}
          </section>

          <section className="task-detail-side-card">
            <h3>Dates</h3>
            <div className="task-detail-dates">
              <div>
                <span className="task-detail-date-label">Created</span>
                <span className="task-detail-date-value">
                  {task.createdAt
                    ? format(new Date(task.createdAt), "MMM d, yyyy")
                    : "—"}
                </span>
              </div>
              <div>
                <span className="task-detail-date-label">Updated</span>
                <span className="task-detail-date-value">
                  {task.updatedAt
                    ? format(new Date(task.updatedAt), "MMM d, yyyy")
                    : "—"}
                </span>
              </div>
            </div>
          </section>
        </aside>
      </div>
    </main>
  );
};

export default TaskDetail;
